import React, {useId, useLayoutEffect, useState} from 'react';
import {create} from 'zustand';

import {Button, Checkbox, Dialogue, Icon, Modal, Text} from '@albert-io/atomic';
import {local} from 'lib/StorageUtil';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {useCanViewAnnotations} from './useCanViewAnnotations';

export const SESSION_ONLY_NOTICE_DISMISSED_KEY = 'session-only-annotation-notice-dismissed';

const DONT_SHOW_AGAIN_CHECKBOX_ID = 'session-only-annotation-notice__dont-show-again';

interface MountedNotice {
  /** The notice on the page, if any. Each mount asks again until it is answered. */
  mountId: string | null;
  isDismissed: boolean;
}

const useMountedNoticeStore = create<MountedNotice>(() => ({mountId: null, isDismissed: false}));

/**
 * Whether the session-only notice is up, for the notice itself and for anything else that
 * opens with the mode and must wait it out rather than stack on it. False wherever no
 * notice is mounted. The lasting opt-out is read once per mount.
 */
export const useIsSessionOnlyNoticeOpen = (): boolean => {
  const isAvailable = useCanViewAnnotations();
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const isMounted = useMountedNoticeStore((state) => state.mountId !== null);
  const isDismissed = useMountedNoticeStore((state) => state.isDismissed);
  const [isOptedOut] = useState(() => local.get(SESSION_ONLY_NOTICE_DISMISSED_KEY, false) === true);

  return isMounted && isAvailable && isAnnotationModeOn && !isDismissed && !isOptedOut;
};

/** Mount only where annotations are session-only; in assignments they persist and this would lie. */
export default function SessionOnlyAnnotationNotice() {
  const mountId = useId();
  const messageId = useId();
  const isOpen = useIsSessionOnlyNoticeOpen();
  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);

  // Before paint, so a mount with the mode already on opens without a blank frame.
  useLayoutEffect(() => {
    useMountedNoticeStore.setState({mountId, isDismissed: false});
    return () => {
      if (useMountedNoticeStore.getState().mountId === mountId) {
        useMountedNoticeStore.setState({mountId: null, isDismissed: false});
      }
    };
  }, [mountId]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    if (isDontShowAgainChecked) {
      local.set(SESSION_ONLY_NOTICE_DISMISSED_KEY, true);
    }
    useMountedNoticeStore.setState({isDismissed: true});
  };

  return (
    <Modal ariaLabel='Heads up!' ariaDescribedBy={messageId} handleClose={handleClose}>
      {() => (
        <Dialogue className='rounded-x2 max-w-[320px]' hideCloseBtn>
          <div className='gap-x5 flex w-full flex-col items-center'>
            <Icon icon='hand' className='fa-2x' iconStyle='light' />
            <div className='gap-x2 flex w-full flex-col items-center'>
              <Text as='h2' bold size='regular' className='m-x0 text-center'>
                Heads up!
              </Text>
              <Text id={messageId} className='text-center'>
                Your highlights and notes will disappear if you refresh or leave this page.
              </Text>
            </div>
            <div className='gap-x2 flex w-full flex-col items-center'>
              <Button
                autoFocus
                className='w-full'
                color='primary'
                onClick={handleClose}
                variant='solid'
              >
                Got it!
              </Button>
              <label
                className='gap-x1 flex cursor-pointer items-center'
                htmlFor={DONT_SHOW_AGAIN_CHECKBOX_ID}
              >
                <Checkbox
                  aria-hidden={false}
                  checked={isDontShowAgainChecked}
                  id={DONT_SHOW_AGAIN_CHECKBOX_ID}
                  onChange={() => setIsDontShowAgainChecked((checked) => !checked)}
                  size='s'
                />
                <Text size='s'>Don&apos;t show this again</Text>
              </label>
            </div>
          </div>
        </Dialogue>
      )}
    </Modal>
  );
}
